import { z } from 'zod';
import { currencySchema, productCategorySchema, raritySchema } from './enums.js';

/** URL-safe slug: lowercase alphanumerics and single hyphens. */
export const slugSchema = z
  .string()
  .min(2)
  .max(80)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Must be a lowercase, hyphen-separated slug');

const priceCentsSchema = z
  .number()
  .int('Price must be whole cents')
  .min(0)
  .max(100_000_00, 'Price out of range');

const stockSchema = z.number().int().min(0).max(1_000_000);
const sortWeightSchema = z.number().int().min(0).max(10_000);

/**
 * Estimated delivery time in MINUTES. Stored as an integer so it stays
 * sortable and unit-agnostic; {@link formatDeliveryTime} renders it
 * ("10 mins", "1 hour", "2 hours"). Capped at one week.
 */
const deliveryMinutesSchema = z.number().int().min(1).max(10_080);

/** Quick-pick options offered in the admin product form. */
export const DELIVERY_TIME_PRESETS = [
  { minutes: 5, label: '5 mins' },
  { minutes: 10, label: '10 mins' },
  { minutes: 15, label: '15 mins' },
  { minutes: 20, label: '20 mins' },
  { minutes: 30, label: '30 mins' },
  { minutes: 45, label: '45 mins' },
  { minutes: 60, label: '1 hour' },
  { minutes: 120, label: '2 hours' },
  { minutes: 360, label: '6 hours' },
  { minutes: 720, label: '12 hours' },
  { minutes: 1440, label: '24 hours' },
] as const;

/** Default estimate applied to products that don't specify one. */
export const DEFAULT_DELIVERY_MINUTES = 30;

/**
 * Human-readable delivery estimate, e.g. 10 -> "10 mins", 60 -> "1 hour",
 * 90 -> "1h 30m". Used identically by the storefront and the admin.
 */
export function formatDeliveryTime(minutes: number): string {
  if (minutes < 60) return `${minutes} min${minutes === 1 ? '' : 's'}`;

  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;

  if (remainder === 0) {
    if (hours === 24) return '24 hours';
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${hours}h ${remainder}m`;
}

/* ------------------------------------------------------------------ */
/* Domain object (API response shape)                                  */
/* ------------------------------------------------------------------ */

export const productSchema = z.object({
  id: z.string().uuid(),
  slug: slugSchema,
  name: z.string().min(2).max(120),
  description: z.string().max(4000),
  category: productCategorySchema,
  rarity: raritySchema,
  priceCents: priceCentsSchema,
  currency: currencySchema,
  stock: stockSchema,
  imageKey: z.string().max(256).nullable(),
  isFeatured: z.boolean(),
  sortWeight: sortWeightSchema,
  deliveryMinutes: deliveryMinutesSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type Product = z.infer<typeof productSchema>;

export const productListSchema = z.array(productSchema);

/* ------------------------------------------------------------------ */
/* Admin write inputs                                                  */
/* ------------------------------------------------------------------ */

export const createProductInputSchema = z.object({
  slug: slugSchema,
  name: z.string().min(2).max(120),
  description: z.string().max(4000).default(''),
  category: productCategorySchema,
  rarity: raritySchema,
  priceCents: priceCentsSchema,
  currency: currencySchema.default('USD'),
  stock: stockSchema.default(0),
  imageKey: z.string().max(256).nullable().default(null),
  isFeatured: z.boolean().default(false),
  sortWeight: sortWeightSchema.default(0),
  deliveryMinutes: deliveryMinutesSchema.default(DEFAULT_DELIVERY_MINUTES),
});

export type CreateProductInput = z.infer<typeof createProductInputSchema>;

/** Every field optional; at least one required (enforced in the route). */
export const updateProductInputSchema = createProductInputSchema.partial();
export type UpdateProductInput = z.infer<typeof updateProductInputSchema>;

/** Bulk stock adjustment (admin inventory tools). */
export const bulkStockAdjustInputSchema = z.object({
  adjustments: z
    .array(
      z.object({
        productId: z.string().uuid(),
        /** Signed delta applied to current stock; result clamped at >= 0. */
        delta: z.number().int(),
      }),
    )
    .min(1)
    .max(200),
});

export type BulkStockAdjustInput = z.infer<typeof bulkStockAdjustInputSchema>;

/* ------------------------------------------------------------------ */
/* Bulk actions (admin multi-select)                                   */
/* ------------------------------------------------------------------ */

export const BULK_ACTIONS = ['delete', 'feature', 'unfeature', 'setStock'] as const;
export const bulkActionSchema = z.enum(BULK_ACTIONS);
export type BulkAction = z.infer<typeof bulkActionSchema>;

export const bulkProductActionInputSchema = z
  .object({
    productIds: z.array(z.string().uuid()).min(1).max(200),
    action: bulkActionSchema,
    /** Required when action is `setStock`. */
    stock: stockSchema.optional(),
  })
  .refine((v) => v.action !== 'setStock' || v.stock !== undefined, {
    message: 'stock is required for the setStock action',
    path: ['stock'],
  });

export type BulkProductActionInput = z.infer<typeof bulkProductActionInputSchema>;

/**
 * Outcome of a bulk action. `skipped` carries per-product reasons (e.g. a
 * product that could not be deleted because it has order history), so the UI
 * can report partial success precisely.
 */
export const bulkProductActionResultSchema = z.object({
  action: bulkActionSchema,
  succeeded: z.number().int().min(0),
  skipped: z.array(z.object({ productId: z.string(), name: z.string(), reason: z.string() })),
});

export type BulkProductActionResult = z.infer<typeof bulkProductActionResultSchema>;

/* ------------------------------------------------------------------ */
/* Public list query (server-side filter / sort / search / paging)     */
/* ------------------------------------------------------------------ */

export const PRODUCT_SORTS = ['newest', 'price_asc', 'price_desc', 'popularity'] as const;
export const productSortSchema = z.enum(PRODUCT_SORTS);
export type ProductSort = z.infer<typeof productSortSchema>;

/**
 * Query params are strings on the wire; this schema coerces them. Used by the
 * public list endpoint and the storefront catalog page.
 */
export const productListQuerySchema = z.object({
  category: productCategorySchema.optional(),
  rarity: raritySchema.optional(),
  search: z.string().trim().max(80).optional(),
  sort: productSortSchema.default('newest'),
  featured: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  inStock: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(48).default(24),
});

export type ProductListQuery = z.infer<typeof productListQuerySchema>;
